import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { filterBy } from "@progress/kendo-data-query";
import { useNominalsBySubType, useAllNominals } from "services/nominalsService";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { v4 as uuidV4 } from "uuid";
import { Form, FormElement, Field } from "@progress/kendo-react-form";
import { AmountFormatCell, formatDateForAPI } from "components/common/formatter";
import { Button } from "@progress/kendo-react-buttons";
import SearchComboBox from "components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";
import { Label } from "@progress/kendo-react-labels";
import { useActiveVats, useVats } from "services/vatService";
import { postMatterExpenses, useActiveExpenses } from "services/expenseService";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import SubmitButton from "components/common/SubmitButton";
import { getMatterDefaults } from "api";
import { toast } from "react-toastify";
import { CrossIcon } from "components/tabs/TabTitle";

const DateCell = (props) => {
  const { dataItem, field, onChange } = props;

  return (
    <td className="p-2">
      <div className={dataItem.hasDateError ? "validation-invalid" : ""}>
        <DatePicker
          value={dataItem.date}
          toggleButton={CalenderCustomToggleButton}
          onChange={(e) => {
            onChange({
              dataItem: dataItem,
              field: field,
              syntheticEvent: e,
              value: e.value,
            });
          }}
          placeholder="Select Date"
          width="100%"
        />
      </div>
    </td>
  );
};

const DetailsCell = (props) => {
  const { dataItem, field, onChange } = props;

  return (
    <td className="p-2">
      {dataItem.inEdit ? (
        <div className={dataItem.hasDetailsError ? "validation-invalid" : ""}>
          <Input
            type="text"
            value={dataItem[field] || ""}
            onChange={(e) => {
              onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: e,
                value: e.value,
              });
            }}
            style={{ width: "100%" }}
          />
        </div>
      ) : (
        dataItem[field]
      )}
    </td>
  );
};

const ValueCellComponent = (props) => {
  const { dataItem, field, onChange } = props;

  return (
    <td className="p-2">
      <div className={dataItem.hasValueError ? "validation-invalid" : ""}>
        <NumericTextBox
          format={props.format || "n2"}
          className="mt-auto right-align"
          value={dataItem[field] !== undefined ? dataItem[field] : 0}
          onChange={(e) => {
            if (onChange) {
              onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: e.syntheticEvent,
                value: e.value,
              });
            }
          }}
          spinners={false}
          style={{ width: "100%" }}
        />
      </div>
    </td>
  );
};

const findVatById = (vatCodeId, vats = []) =>
  vats.find((item) => String(item?.vatCodeId) === String(vatCodeId));

const resolveVatRate = (item, netAmount, vatAmount, vatRate) => {
  const explicitRate = Number(vatRate ?? item?.vatRate);
  if (Number.isFinite(explicitRate)) {
    return explicitRate;
  }
  const net = Number(netAmount);
  const vat = Number(vatAmount);
  if (Number.isFinite(net) && Number.isFinite(vat) && net !== 0) {
    return (vat / net) * 100;
  }
  return 0;
};

const isRowEmpty = (row) => {
  const hasDetails = Boolean(row.details?.trim());
  const hasValue = Number(row.netAmount) > 0;
  return !hasDetails && !hasValue;
};

const ExpenseRequest = ({ matter, cancelEdit, onSubmitSuccess }) => {
  const initialData = [
    {
      timeId: uuidV4(),
      type: "Expense",
      date: new Date(),
      details: "",
      netAmount: 0,
      vatAmount: 0,
      gross: 0,
      vatCode: 0,
      inEdit: true,
    },
  ];

  const [data, setData] = React.useState(initialData);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const submitAttemptedRef = useRef(false);
  const [gridErrors, setGridErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSelectExpense, setShowSelectExpense] = useState(false);
  const [selectedExpenseTemplate, setSelectedExpenseTemplate] = useState(null);
  const [vatvalue, setvatvalue] = useState();
  const [vatdata, setvatdata] = React.useState();
  const [nominal, setnominal] = useState();
  const [nominaldata, setnominaldata] = React.useState();
  const [matterDefaults, setMatterDefaults] = useState(null);
  const vatDefaultAppliedRef = useRef(false);
  const nominalDefaultAppliedRef = useRef(false);

  const { data: activevatsdata = [] } = useActiveVats();
  const { data: allVats = [] } = useVats();
  const { data: allNominals = [] } = useAllNominals();
  const { data: activeExpenses = [] } = useActiveExpenses();
  const { data: expenseNominals = [] } = useNominalsBySubType(
    "ProfitAndLoss_Expense",
    { excludeArchived: true },
  );

  const recalculateRowAmounts = useCallback((rows, vat) => {
    if (!vat) return rows;
    return rows.map((item) => {
      if (item.netAmount !== undefined && item.netAmount !== null) {
        const vatAmount = (vat.rate / 100) * item.netAmount;
        return {
          ...item,
          vatAmount,
          gross: item.netAmount + vatAmount,
        };
      }
      return item;
    });
  }, []);

  const addNew = () => {
    const newDataItem = {
      inEdit: true,
      timeId: uuidV4(),
      type: "Expense",
      date: new Date(),
      details: "",
      netAmount: 0,
      vatAmount: 0,
      gross: 0,
      vatCode: 0,
    };
    setData((prev) => [...prev, newDataItem]);
    if (gridErrors.length > 0) setGridErrors([]);
  };

  const removeRow = (item) => {
    setData((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((row) => row.timeId !== item.timeId);
    });
    if (gridErrors.length > 0) setGridErrors([]);
  };

  const DeleteCommandCell = (props) => (
    <td className="p-2">
      <CrossIcon
        disabled={data.length <= 1}
        onClick={() => removeRow(props.dataItem)}
        className="cursor-pointer"
      />
    </td>
  );

  const onChangenominal = useCallback((event) => {
    setnominal(event?.value || null);
  }, []);

  const onChangevat = useCallback(
    (event) => {
      setvatvalue(event.value);
      if (event.value !== null && event.value !== undefined) {
        setData((prev) => recalculateRowAmounts(prev, event.value));
      }
    },
    [recalculateRowAmounts],
  );

  const filternominalData = (filter) => {
    const list = expenseNominals.slice();
    return filterBy(list, filter);
  };

  const filternominal = (event) => {
    setnominaldata(filternominalData(event.filter));
  };

  const filtervatData = (filter) => {
    const list = activevatsdata.slice();
    return filterBy(list, filter);
  };

  const filtervat = (event) => {
    setvatdata(filtervatData(event.filter));
  };

  const applyExpenseTemplate = (template) => {
    if (!template) return;

    const vat =
      findVatById(template.vatCode, allVats) ||
      findVatById(template.vatCode, activevatsdata);
    const nominalMatch = expenseNominals.find(
      (item) => item.nominalRef === template.nominalRef,
    );

    if (vat) {
      onChangevat({ value: vat });
    }
    if (nominalMatch) {
      onChangenominal({ value: nominalMatch });
    }

    const netAmount = template.netAmount ?? 0;
    const vatAmount = template.vatAmount ?? 0;
    const templateRow = {
      inEdit: true,
      timeId: uuidV4(),
      type: "Expense",
      date: new Date(),
      details: template.details || "",
      netAmount,
      vatAmount,
      gross: netAmount + vatAmount,
      vatCode: template.vatCode,
    };

    setData((prev) => {
      if (!prev.length) {
        return [templateRow];
      }

      const lastRow = prev[prev.length - 1];
      if (isRowEmpty(lastRow)) {
        return [
          ...prev.slice(0, -1),
          { ...templateRow, timeId: lastRow.timeId },
        ];
      }

      return [...prev, templateRow];
    });

    setSelectedExpenseTemplate(null);
    setShowSelectExpense(false);
    if (gridErrors.length > 0) setGridErrors([]);
  };

  const openSelectExpenseDialog = () => {
    setSelectedExpenseTemplate(null);
    setShowSelectExpense(true);
  };

  const closeSelectExpenseDialog = () => {
    setSelectedExpenseTemplate(null);
    setShowSelectExpense(false);
  };

  const handleSubmit = async (values) => {
    submitAttemptedRef.current = true;
    setSubmitAttempted(true);

    const currentVat = vatvalue;
    const currentNominal = nominal;

    if (!currentVat || !currentNominal || !values.feeEarner) {
      return;
    }

    const errors = [];
    const hasInvalidRows = data.some((item, index) => {
      const rowErrors = [];

      if (
        !item.date ||
        !(item.date instanceof Date) ||
        isNaN(item.date.getTime())
      ) {
        rowErrors.push("Date is required");
      }
      if (!item.details || item.details.trim() === "") {
        rowErrors.push("Details is required");
      }
      if (!item.netAmount || item.netAmount <= 0) {
        rowErrors.push("Value must be greater than 0");
      }

      if (rowErrors.length > 0) {
        errors.push({ rowIndex: index + 1, errors: rowErrors });
        return true;
      }
      return false;
    });

    if (hasInvalidRows) {
      setGridErrors(errors);
      return;
    }

    setGridErrors([]);
    setSubmitting(true);

    try {
      const feeEarnerRef = values.feeEarner?.userRef;
      const nominalRef = currentNominal?.nominalRef;
      const vatCodeId = currentVat?.vatCodeId;
      const vatRate = currentVat?.rate;

      const requestData = data.map((item) => {
        const netAmount = Number(item.netAmount) || 0;
        const vatAmount = Number(item.vatAmount) || 0;
        return {
          details: item.details?.trim(),
          date: formatDateForAPI(item.date),
          netAmount,
          vatCode: vatCodeId,
          vatRate: resolveVatRate(item, netAmount, vatAmount, vatRate),
          vatAmount,
          nominalRef,
          active: true,
          feeEarnerRef,
        };
      });

      await postMatterExpenses(matter?.matterId, requestData);
      toast.success("Expense Request submitted");
      onSubmitSuccess?.();
      cancelEdit?.();
    } catch (submitError) {
      console.error(submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const itemChange = (e) => {
    setData((prev) => {
      const updated = prev.map((item) => {
        if (item.timeId === e.dataItem.timeId) {
          const nextItem = { ...item, [e.field || ""]: e.value };
          if (e.field === "netAmount" && vatvalue) {
            const vatAmount = (vatvalue.rate / 100) * (e.value || 0);
            nextItem.vatAmount = vatAmount;
            nextItem.gross = (e.value || 0) + vatAmount;
          }
          return nextItem;
        }
        return item;
      });
      return updated;
    });

    if (gridErrors.length > 0) setGridErrors([]);
  };

  useEffect(() => {
    if (activevatsdata.length > 0) {
      setvatdata(activevatsdata.slice());
    }
  }, [activevatsdata]);

  useEffect(() => {
    if (expenseNominals.length > 0) {
      setnominaldata(expenseNominals.slice());
    }
  }, [expenseNominals]);

  useEffect(() => {
    vatDefaultAppliedRef.current = false;
    nominalDefaultAppliedRef.current = false;
  }, [matter?.matterId]);

  useEffect(() => {
    const fetchMatterDefaults = async () => {
      if (!matter?.matterId) return;
      try {
        const response = await getMatterDefaults(matter.matterId);
        setMatterDefaults(response?.data?.data ?? null);
      } catch (loadError) {
        console.error(loadError);
      }
    };

    fetchMatterDefaults();
  }, [matter?.matterId]);

  const resolveDefaultNominal = useCallback(
    (nominalRef) => {
      if (nominalRef == null || nominalRef === "") return null;

      const ref = String(nominalRef);
      const fromExpenseList = expenseNominals.find(
        (item) => String(item.nominalRef) === ref,
      );
      if (fromExpenseList) return fromExpenseList;

      return (
        allNominals.find(
          (item) =>
            item.archived !== true && String(item.nominalRef) === ref,
        ) || null
      );
    },
    [expenseNominals, allNominals],
  );

  useEffect(() => {
    if (
      vatDefaultAppliedRef.current ||
      matterDefaults?.defaultVatCodeId == null ||
      (!allVats.length && !activevatsdata.length)
    ) {
      return;
    }

    const defaultVat =
      findVatById(matterDefaults.defaultVatCodeId, allVats) ||
      findVatById(matterDefaults.defaultVatCodeId, activevatsdata);

    vatDefaultAppliedRef.current = true;

    if (!defaultVat) return;
    onChangevat({ value: defaultVat });
    setvatdata((prev) => {
      const list = prev?.length ? prev.slice() : activevatsdata.slice();
      if (
        !list.some(
          (item) =>
            String(item.vatCodeId) === String(defaultVat.vatCodeId),
        )
      ) {
        return [...list, defaultVat];
      }
      return list;
    });
  }, [matterDefaults, allVats, activevatsdata, onChangevat]);

  useEffect(() => {
    if (
      nominalDefaultAppliedRef.current ||
      !matterDefaults ||
      (!expenseNominals.length && !allNominals.length)
    ) {
      return;
    }

    const defaultNominalRef =
      matterDefaults.defaultProfitNominal ?? matter?.defaultProfitCost;
    const defaultNominal = resolveDefaultNominal(defaultNominalRef);

    nominalDefaultAppliedRef.current = true;

    if (!defaultNominal) return;
    setnominal(defaultNominal);
    setnominaldata((prev) => {
      const list = prev?.length ? prev.slice() : expenseNominals.slice();
      if (
        !list.some(
          (item) =>
            String(item.nominalRef) === String(defaultNominal.nominalRef),
        )
      ) {
        return [...list, defaultNominal];
      }
      return list;
    });
  }, [
    matterDefaults,
    matter?.defaultProfitCost,
    expenseNominals,
    allNominals,
    resolveDefaultNominal,
  ]);

  const dataWithValidation = data.map((item, index) => {
    const rowIndex = index + 1;
    const rowError = gridErrors.find((err) => err.rowIndex === rowIndex);

    return {
      ...item,
      hasDetailsError:
        submitAttempted &&
        rowError?.errors.some((e) => e.includes("Details")),
      hasValueError:
        submitAttempted && rowError?.errors.some((e) => e.includes("Value")),
      hasDateError:
        submitAttempted && rowError?.errors.some((e) => e.includes("Date")),
    };
  });

  return (
    <>
      <Dialog
        title="Expenses"
        onClose={cancelEdit}
        className="dialog-xl"
      >
        <div
          className="d-flex flex-column h-100 overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <div className="flex-shrink-0 pt-4 pb-3 px-5">
            <div className="d-flex justify-content-end mb-3">
              <Button
                className="common-btn setting-btn"
                onClick={openSelectExpenseDialog}
              >
                Select Expense
              </Button>
            </div>

            <div className="validation border rounded px-3 py-3 shadow-sm">
              <Form
                ignoreModified={true}
                onSubmit={handleSubmit}
                id="expenseRequestForm"
                render={(formRenderProps) => (
                  <FormElement id="expenseRequestForm" noValidate>
                    <fieldset>
                      <div className="d-flex flex-wrap align-items-end">
                        <div
                          className={`d-flex flex-column mb-3 me-3 ${submitAttempted && !vatvalue
                            ? "validation-invalid"
                            : ""
                            }`}
                          style={{ width: "300px" }}
                        >
                          <Label>Vat Code</Label>
                          <ComboBox
                            style={{ width: "300px" }}
                            className="mt-1"
                            data={vatdata || activevatsdata}
                            dataItemKey="vatCodeId"
                            textField="display"
                            value={vatvalue}
                            iconClassName="wa"
                            filterable={true}
                            onFilterChange={filtervat}
                            onChange={onChangevat}
                            placeholder="Search VAT Code"
                          />
                        </div>

                        <div
                          className={`d-flex flex-column mb-3 me-3 ${submitAttempted && !nominal
                            ? "validation-invalid"
                            : ""
                            }`}
                          style={{ width: "300px" }}
                        >
                          <Label>Nominal Ref</Label>
                          <ComboBox
                            className="mt-1"
                            style={{ width: "300px" }}
                            data={nominaldata || expenseNominals}
                            dataItemKey="nominalRef"
                            iconClassName="wa"
                            textField="display"
                            filterable={true}
                            onFilterChange={filternominal}
                            value={nominal}
                            onChange={onChangenominal}
                            placeholder="Search Nominal Ref"
                          />
                        </div>

                        <div
                          className={`mb-3 ${submitAttempted &&
                            !formRenderProps.valueGetter("feeEarner")
                            ? "validation-invalid"
                            : ""
                            }`}
                          style={{ width: "300px" }}
                        >
                          <Field
                            key={
                              matterDefaults
                                ? matterDefaults.matterFeeEarner ?? "loaded"
                                : "pending"
                            }
                            name="feeEarner"
                            component={SearchComboBox}
                            cbType={CONSTANT.FE}
                            customValidation={true}
                            className="mt-1"
                            iconClassName="wa"
                            width="300px"
                            placeholder="Search Fee Earner Ref"
                            defaultValues={matterDefaults}
                            defaultValueSetter={formRenderProps}
                          />
                        </div>
                      </div>
                    </fieldset>
                  </FormElement>
                )}
              />
            </div>
          </div>

          <div
            className="flex-grow-1 d-flex flex-column px-5 pb-3 mt-2"
            style={{ minHeight: 0 }}
          >
            <Grid
              data={dataWithValidation}
              className="grid-header-sm"
              style={{ height: "100%", minHeight: "180px" }}
              editField="inEdit"
              onItemChange={itemChange}
              dataItemKey="timeId"
            >
              <Column
                field="date"
                title="Date"
                format="{0:d}"
                width="200px"
                headerClassName="ps-3"
                cell={DateCell}
              />
              <Column
                field="details"
                title="Details"
                width="200px"
                cell={DetailsCell}
              />
              <Column
                field="netAmount"
                editor="numeric"
                title="Value"
                cell={ValueCellComponent}
                className="p-2"
              />
              <Column
                field="vatAmount"
                title="Vat"
                editable={false}
                cell={AmountFormatCell}
                className="p-2"
                headerClassName="grid-header-center"
              />
              <Column
                field="gross"
                title="Gross"
                editable={false}
                cell={AmountFormatCell}
                className="p-2"
                headerClassName="grid-header-center"
              />
              <Column cell={DeleteCommandCell} title="" width="50px" />
            </Grid>

            <div className="flex-shrink-0">
              <Button
                title="Add new"
                className="common-btn thirdButton"
                style={{ marginTop: "10px" }}
                onClick={addNew}
              >
                Add New Line
              </Button>
            </div>
          </div>
        </div>

        <DialogActionsBar>
          <div className="dialog-footer w-100 d-flex justify-content-end">
            <Button
              type="button"
              className="common-btn bgBtnSecondary border-0 me-3"
              onClick={cancelEdit}
            >
              Cancel
            </Button>
            <SubmitButton
              type="submit"
              form="expenseRequestForm"
              className="me-4"
              isLoading={submitting}
              onClick={() => {
                submitAttemptedRef.current = true;
                setSubmitAttempted(true);
              }}
            >
              Request
            </SubmitButton>
          </div>
        </DialogActionsBar>
      </Dialog>

      {showSelectExpense && (
        <Dialog
          title="Select Expense"
          onClose={closeSelectExpenseDialog}
          width={600}
        >
          <div className="d-flex flex-column px-4 pt-3 pb-4">
            <Label className="label mb-2 me-2">Expense Template</Label>
            <ComboBox
              data={activeExpenses}
              textField="details"
              dataItemKey="expenseId"
              placeholder="Select expense template"
              value={selectedExpenseTemplate}
              onChange={(e) => setSelectedExpenseTemplate(e.value)}
            />
          </div>
          <DialogActionsBar>
            <div className="dialog-footer w-100 d-flex justify-content-end">
              <Button
                className="common-btn bgBtnSecondary border-0 me-3"
                onClick={closeSelectExpenseDialog}
              >
                Cancel
              </Button>
              <Button
                className="common-btn me-3"
                disabled={!selectedExpenseTemplate}
                onClick={() => applyExpenseTemplate(selectedExpenseTemplate)}
              >
                Add
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default ExpenseRequest;
